import React, { useEffect } from 'react';
import cx from 'classnames';
import loadSrc from '@zola-helpers/client/dist/es/util/loadSrc';
import {
  LoginResult,
  sendAppleLoginRequest,
  ErrorType,
} from '@zola-helpers/client/dist/es/redux/auth/authActions';

import { HostName } from '../../types';
import useGuessTimezone from '../../../../hooks/useGuessTimezone';

import styles from './AppleLoginButton.module.less';

export enum AppleLoginDataTypes {
  SIGNUP = 'sign-up',
  SIGNIN = 'sign-in',
}

type AppleLoginButtonProps = {
  authState?: AppleLoginDataTypes;
  callback: (data: LoginResult, email?: string) => void;
  errorCallback: (response: ErrorType) => void;
};
// Derived from https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_js/configuring_your_webpage_for_sign_in_with_apple
type AppleLoginResponse = {
  detail?: {
    authorization?: {
      state?: string;
      code?: string;
      id_token?: string;
    };
    user?: {
      email?: string;
      name?: {
        firstName?: string;
        lastName?: string;
      };
    };
  };
};

const AppleLoginButton: React.FC<AppleLoginButtonProps> = ({
  authState = AppleLoginDataTypes.SIGNIN,
  callback,
  errorCallback,
}) => {
  const [timezone] = useGuessTimezone();
  const redirectURI = () => {
    const hostname = window?.location?.hostname.toLowerCase();
    switch (hostname) {
      case HostName.LOCALHOST:
      case HostName.LOCALHOST_TEST:
        return 'http://local.test';
      case HostName.QA:
        return 'https://qa.zola.com';
      case HostName.QA_HOMESTORE:
        return 'https://qa-homestore.zola.com';
      case HostName.PROD_HOMESTORE:
        return 'https://homestore.zola.com';
      case HostName.PROD:
      default:
        return 'https://www.zola.com';
    }
  };

  const appleSignInSuccess = (appleLoginResponse: AppleLoginResponse) => {
    const authCode = appleLoginResponse?.detail?.authorization?.code;
    const requestData = {
      authorization_code: authCode,
      timezone,
    };
    if (authCode) {
      sendAppleLoginRequest(requestData).then(callback).catch(errorCallback);
    }
  };

  const appleInit = () => {
    // Work to do after the library loads.
    const { AppleID } = window;
    if (AppleID && AppleID.auth) {
      AppleID.auth.init({
        clientId: 'com.zola.services',
        scope: 'email',
        redirectURI: redirectURI(),
        usePopup: true, // or false defaults to false
      });
    }
  };

  const onAppleSignIn = (data: Event | AppleLoginResponse) => {
    // handle successful response
    appleSignInSuccess(data as AppleLoginResponse);
  };

  useEffect(() => {
    loadSrc(
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
      {
        id: 'apple-auth-src',
        type: 'text/javascript',
      },
      appleInit
    );

    // Listen for authorization success
    if (document.addEventListener) {
      document.addEventListener('AppleIDSignInOnSuccess', onAppleSignIn);
    } else {
      // IE 11 support
      document.attachEvent('onAppleIDSignInOnSuccess', onAppleSignIn);
    }

    return () => {
      document.removeEventListener('AppleIDSignInOnSuccess', onAppleSignIn);
    };
  }, [authState]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      id="appleid-signin"
      className={cx('v2-button primary', styles.btn)}
      data-color="black"
      data-type={authState}
      data-border-radius="50"
    />
  );
};

export default AppleLoginButton;
